/* eslint-disable react-refresh/only-export-components */
import { RotateCcw } from "lucide-react";
import type { RaRecord } from "ra-core";
import {
  EditBase,
  Form,
  required,
  useGetList,
  useNotify,
  useRequireAccess,
  useTranslate,
} from "ra-core";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { ArrayInput } from "@/components/admin/array-input";
import { SaveButton } from "@/components/admin/form";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toSlug } from "@/lib/toSlug";

import {
  useConfigurationContext,
  useConfigurationUpdater,
  type ConfigurationContextValue,
} from "../root/ConfigurationContext";
import { defaultConfiguration } from "../root/defaultConfiguration";
import type { SponsoringStage } from "../types";

/** The role a stage plays in the application. */
export type StageRole = "board" | "active" | "closed";

type FormStage = { value?: string; label: string };

/** The value a stage will be stored under (slug of the label when new). */
const stageValue = (stage: FormStage) =>
  stage.value || (stage.label ? toSlug(stage.label) : "");

/** Ensure every stage has a value (slugged from its label). */
const ensureValues = (stages: FormStage[] | undefined) =>
  stages?.map((stage) => ({ ...stage, value: stageValue(stage) }));

export type ValidateStagesMessages = {
  duplicate: (items: string[]) => string;
  inUse: (items: string[]) => string;
  validating: string;
};

/**
 * Reject duplicate stage values and the removal of a stage that sponsorings
 * still use. Returns undefined when the stage list is valid.
 */
export const validateStagesInUse = (
  stages: FormStage[] | undefined,
  sponsorings: RaRecord[] | undefined,
  messages: ValidateStagesMessages,
) => {
  if (!stages) return undefined;

  const values = stages.map(stageValue);
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  if (duplicates.size > 0) {
    return messages.duplicate([...duplicates]);
  }

  // Sponsorings are still loading: block the save until we can check.
  if (!sponsorings) return messages.validating;

  const known = new Set(values);
  const inUse = [
    ...new Set(
      sponsorings
        .map((sponsoring) => sponsoring.stage as string)
        .filter((stage) => stage && !known.has(stage)),
    ),
  ];
  return inUse.length > 0 ? messages.inUse(inUse) : undefined;
};

/** The chosen role must stand out from the two other options. */
const roleItemClass =
  "data-[state=on]:bg-primary data-[state=on]:text-primary-foreground max-sm:h-auto max-sm:min-w-0 max-sm:flex-1 max-sm:px-1 max-sm:py-1.5 max-sm:text-xs max-sm:leading-tight max-sm:whitespace-normal";

const transformFormValues = (data: Record<string, any>) => ({
  config: {
    title: data.title,
    logo: defaultConfiguration.logo,
    sponsoringStages: ensureValues(data.sponsoringStages),
    sponsoringActiveStatuses: data.sponsoringActiveStatuses,
    sponsoringClosedStatuses: data.sponsoringClosedStatuses,
  } as ConfigurationContextValue,
});

/**
 * Admin-only settings: the application title and the sponsoring stages
 * (their labels and the role each stage plays: board, active or closed).
 */
export const SettingsPage = () => {
  const { isPending: isAccessPending } = useRequireAccess({
    resource: "configuration",
    action: "edit",
  });
  const updateConfiguration = useConfigurationUpdater();
  const notify = useNotify();

  if (isAccessPending) return null;

  return (
    <EditBase
      resource="configuration"
      id={1}
      mutationMode="pessimistic"
      redirect={false}
      transform={transformFormValues}
      mutationOptions={{
        onSuccess: (data: any) => {
          updateConfiguration(data.config);
          notify("crm.settings.saved");
        },
        onError: () => {
          notify("crm.settings.save_error", { type: "error" });
        },
      }}
    >
      <SettingsForm />
    </EditBase>
  );
};

SettingsPage.path = "/settings";

const SettingsForm = () => {
  const config = useConfigurationContext();

  const defaultValues = useMemo(
    () => ({
      title: config.title,
      sponsoringStages: config.sponsoringStages,
      sponsoringActiveStatuses: config.sponsoringActiveStatuses,
      sponsoringClosedStatuses: config.sponsoringClosedStatuses,
    }),
    [config],
  );

  return (
    <Form defaultValues={defaultValues}>
      <SettingsFormFields />
    </Form>
  );
};

const SettingsFormFields = () => {
  const translate = useTranslate();
  const { watch, setValue, reset } = useFormContext();

  const stages: SponsoringStage[] = watch("sponsoringStages") ?? [];
  const activeStatuses: string[] = watch("sponsoringActiveStatuses") ?? [];
  const closedStatuses: string[] = watch("sponsoringClosedStatuses") ?? [];

  const { data: sponsorings } = useGetList("sponsorings", {
    pagination: { page: 1, perPage: 1000 },
  });

  const validateStages = useCallback(
    (value: FormStage[] | undefined) =>
      validateStagesInUse(value, sponsorings, {
        duplicate: (items) =>
          translate("crm.settings.validation.duplicate", {
            items: items.join(", "),
          }),
        inUse: (items) =>
          translate("crm.settings.validation.in_use", {
            items: items.join(", "),
          }),
        validating: translate("crm.settings.validation.validating"),
      }),
    [sponsorings, translate],
  );

  const setRole = (value: string, role: StageRole) => {
    setValue(
      "sponsoringActiveStatuses",
      role === "active"
        ? [...activeStatuses.filter((item) => item !== value), value]
        : activeStatuses.filter((item) => item !== value),
      { shouldDirty: true },
    );
    setValue(
      "sponsoringClosedStatuses",
      role === "closed"
        ? [...closedStatuses.filter((item) => item !== value), value]
        : closedStatuses.filter((item) => item !== value),
      { shouldDirty: true },
    );
  };

  return (
    <div className="max-w-2xl space-y-6 mt-2 pb-8">
      {/* On mobile the page title lives in the fixed header of <MobilePage>. */}
      <h1 className="text-2xl font-semibold max-sm:hidden">
        {translate("crm.settings.title")}
      </h1>

      <Card>
        <CardContent className="space-y-4">
          <TextInput
            source="title"
            label="crm.settings.app_title"
            helperText={false}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">
              {translate("crm.settings.sponsorings.stages")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {translate("crm.settings.sponsorings.stages_help")}
            </p>
          </div>

          <ArrayInput
            source="sponsoringStages"
            label={false}
            helperText={false}
            validate={validateStages}
          >
            <SimpleFormIterator disableClear>
              <TextInput
                source="label"
                label={false}
                validate={required()}
                helperText={false}
              />
            </SimpleFormIterator>
          </ArrayInput>

          <div className="space-y-3 pt-2">
            <div>
              <h3 className="text-base font-medium">
                {translate("crm.settings.sponsorings.statuses")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {translate("crm.settings.sponsorings.statuses_help")}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {stages.map((stage) => {
                const value = stageValue(stage);
                const role: StageRole = activeStatuses.includes(value)
                  ? "active"
                  : closedStatuses.includes(value)
                    ? "closed"
                    : "board";
                return (
                  <div
                    key={value}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm max-sm:flex-col max-sm:items-stretch"
                  >
                    <span className="min-w-40 font-medium">
                      {stage.label || value}
                    </span>
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={role}
                      onValueChange={(next) =>
                        next ? setRole(value, next as StageRole) : undefined
                      }
                      aria-label={stage.label || value}
                      className="max-sm:w-full max-sm:items-stretch"
                    >
                      <ToggleGroupItem value="board" className={roleItemClass}>
                        {translate("crm.settings.sponsorings.board_stage")}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="active" className={roleItemClass}>
                        {translate("crm.settings.sponsorings.active_statuses")}
                      </ToggleGroupItem>
                      <ToggleGroupItem value="closed" className={roleItemClass}>
                        {translate("crm.settings.sponsorings.closed_statuses")}
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-4 max-sm:flex-col-reverse max-sm:items-stretch">
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            reset({
              title: defaultConfiguration.title,
              sponsoringStages: defaultConfiguration.sponsoringStages,
              sponsoringActiveStatuses:
                defaultConfiguration.sponsoringActiveStatuses,
              sponsoringClosedStatuses:
                defaultConfiguration.sponsoringClosedStatuses,
            })
          }
        >
          <RotateCcw />
          {translate("crm.settings.reset_defaults")}
        </Button>
        <SaveButton />
      </div>
    </div>
  );
};
